import {
  ResourceTypeId,
  RESOURCE_ROOT_FIELDS,
  RESOURCE_STATIC_FIELDS,
} from '~/client/src/shared/constants/resourceTypes'

interface SplitResult {
  templateId: string | null | undefined
  rootFields: Record<string, any>
  dataFields: Record<string, any>
}

export function splitResourceFormData(
  resourceTypeId: ResourceTypeId,
  formData: Record<string, any>,
  additionalRootFields: readonly string[] = [],
): SplitResult {
  const { _templateId, ...rest } = formData
  const templateId = _templateId as string | null | undefined

  const rootFieldNames = new Set<string>([
    ...(RESOURCE_STATIC_FIELDS[resourceTypeId] || []).map(f => f.name),
    ...(RESOURCE_ROOT_FIELDS[resourceTypeId] || []),
    ...additionalRootFields,
  ])

  const rootFields: Record<string, any> = {}
  const dataFields: Record<string, any> = {}

  for (const [key, value] of Object.entries(rest)) {
    const cleaned = value === '' ? null : value
    if (rootFieldNames.has(key)) {
      rootFields[key] = cleaned
    } else {
      dataFields[key] = cleaned
    }
  }

  return { templateId, rootFields, dataFields }
}

/** An empty resource value: null, undefined, '', or []. `0` and `false` are not. */
function isEmptyResourceValue(value: any): boolean {
  if (value === null || value === undefined || value === '') return true
  return Array.isArray(value) && value.length === 0
}

/**
 * Drops empty entries (null, undefined, '', empty array) from a resource data
 * map so cleared/empty fields are removed from the resource on save rather than
 * persisted as null keys. `0` and `false` are kept.
 */
export function compactResourceData(
  data: Record<string, any>,
): Record<string, any> {
  const result: Record<string, any> = {}
  for (const [key, value] of Object.entries(data)) {
    if (!isEmptyResourceValue(value)) result[key] = value
  }
  return result
}

/**
 * Merges this submission's edits onto an existing resource's data, dropping only
 * the keys the user cleared in this submission. Untouched keys are preserved
 * verbatim — empties never reach persisted data (creation runs through
 * compactResourceData, and a one-off migration removed legacy empty keys), so
 * there is nothing stale to strip from `existingData`.
 */
export function mergeResourceData(
  existingData: Record<string, any>,
  dataFields: Record<string, any>,
): Record<string, any> {
  const result: Record<string, any> = { ...existingData }
  for (const [key, value] of Object.entries(dataFields)) {
    if (isEmptyResourceValue(value)) {
      delete result[key]
    } else {
      result[key] = value
    }
  }
  return result
}
